import { COUNTRIES, COUNTRY_CODES } from "@ondo/shared";

/**
 * Copy for the location panel on a work's record.
 *
 * The one question it exists to ask is `ask`. It is shown to an OWNER whose
 * work has no location — which, since 20260925100000, is every work the
 * moment title moves to them: the seller's shelf goes with the seller's title
 * and the record says nothing until the new owner says something.
 */
export const LOCATION_COPY = {
  title: "Location",

  /** The panel when nothing is recorded. */
  none: "Not recorded.",
  /** …and the reader may record one. The question, and why it is being asked. */
  ask: "Where is it now?",
  askDetail:
    "The record has no location for this work — a sale clears the seller’s. Record where it is kept, or leave it unrecorded. It follows the collection: whoever you share the collection with may read it.",
  /** …and the reader may not. */
  noneReadOnly: "Not recorded.",

  /** The two openers. Different words from the form’s submit, so a screen reader hears one “Record location” at a time. */
  record: "Record location",
  update: "Update location",

  /** The label on the structured pair, shown under the typed line. */
  region: "Country and city",

  form: {
    title: "Where the work is",
    kind: "Where",
    place: "Place",
    placeHelp:
      "‘Sitting room, London’, ‘Crozier, Newark’. Free text — an address is not a registry fact.",
    country: "Country",
    countryHelp:
      "Optional, and the only part of a location anything can ever publish as a region. Your own wording above is never published: it follows the collection, like it always has.",
    countryNone: "Not recorded",
    city: "City",
    cityHelp: "Optional, and only alongside a country. ‘Geneva’ — a city, not an address.",
    submit: "Save location",
    saving: "Saving…",
    cancel: "Cancel",
  },

  /** After a save. A status, so it is announced. */
  recorded: "Location recorded.",
  unknownError: "The location could not be recorded. Please try again.",
  /** The one rule the form answers itself — `describeDraftFault`. */
  cityNeedsCountry: "Choose a country as well: a city on its own is not a region.",
} as const;

/**
 * Every country, by name, for the form's one `<select>`.
 *
 * Sorted by NAME rather than by code, because that is the order a reader scans
 * — and sorted here, once, rather than in a render. `localeCompare` so the
 * accented names land where a reader expects them (Åland, Côte d’Ivoire) rather
 * than after Z.
 */
export const COUNTRY_OPTIONS: readonly { code: string; name: string }[] = COUNTRY_CODES.map(
  (code) => ({ code, name: COUNTRIES[code] }),
).sort((a, b) => a.name.localeCompare(b.name, "en"));
